import PropTypes from 'prop-types';
import { Close, Search } from '@mui/icons-material';
import { IconButton, InputAdornment, OutlinedInput, Tooltip } from '@mui/material';

// One search input shared by both boards, at board level and at section level, so the
// My Task board and the project board cannot drift apart.
const BoardSearchField = ({ value, onChange, onClose, placeholder, compact, autoFocus }) => {
  // A closable field (the section-level one) is dismissed outright: it clears its query
  // on the way out, so a hidden field can never leave a column silently filtered.
  const handleClose = () => {
    onChange('');
    if (onClose) onClose();
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (onClose) handleClose();
    else if (value) onChange('');
  };

  return (
    <OutlinedInput
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={handleKeyDown}
      autoFocus={autoFocus}
      placeholder={placeholder}
      size="small"
      fullWidth={compact}
      sx={
        compact
          ? { mb: 1, backgroundColor: 'background.paper', '& input': { py: 0.5, fontSize: 13 } }
          : { width: 260, backgroundColor: 'background.paper' }
      }
      startAdornment={
        <InputAdornment position="start">
          <Search fontSize="small" sx={{ color: 'text.disabled' }} />
        </InputAdornment>
      }
      endAdornment={
        // A closable field always shows the button, so there is a visible way out even
        // before anything is typed. The board-level field can only be cleared.
        onClose ? (
          <InputAdornment position="end">
            <Tooltip title="Close search" placement="top" arrow>
              <IconButton size="small" aria-label="Close search" onClick={handleClose}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ) : (
          value && (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Clear search" onClick={() => onChange('')}>
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        )
      }
      inputProps={{ 'aria-label': placeholder }}
    />
  );
};

BoardSearchField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  placeholder: PropTypes.string,
  compact: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

BoardSearchField.defaultProps = {
  value: '',
  placeholder: 'Search tasks',
  compact: false,
  autoFocus: false,
};

export default BoardSearchField;
